import React, { useEffect, useMemo, useState } from 'react';
import { buildVideoSourceMeta, formatInText } from '../../lib/apaSlideFormatter.js';
import {
  hasPlaylistCitationDetails,
  normalizePlaylistBlock,
  playlistCountLabel,
  playlistEmbedUrl,
  playlistWatchUrl,
  videoThumbnailUrl
} from '../../utils/youtubePlaylist.js';

const ASPECT_RATIOS = {
  '16-9': '16 / 9',
  '4-3': '4 / 3',
  '1-1': '1 / 1',
  '21-9': '21 / 9'
};

const THEMES = {
  light: {
    surface: '#ffffff',
    border: '#e2e8f0',
    heading: '#0f172a',
    body: '#475569',
    muted: '#64748b',
    rail: '#f8fafc',
    rowHover: '#f1f5f9',
    rowActive: '#eef2ff'
  },
  dark: {
    surface: '#0f172a',
    border: '#1e293b',
    heading: '#f8fafc',
    body: '#cbd5e1',
    muted: '#94a3b8',
    rail: '#111c33',
    rowHover: '#1e293b',
    rowActive: '#1e293b'
  }
};

const PlayGlyph = ({ color = '#ffffff', size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" aria-hidden="true" focusable="false">
    <path d="M3 1.5 L10 6 L3 10.5 Z" fill={color} />
  </svg>
);

/**
 * A YouTube playlist as one shareable card: the player on the left, the
 * playlist's own running order on the right, and the link to hand out
 * underneath. Choosing a row swaps the player rather than sending anyone off to
 * youtube.com mid-lesson.
 *
 * The block keeps its citation under the video block's field names, so the APA
 * note, the deck's reference list and the tear-off-citation action all treat it
 * exactly like a video.
 */
const YouTubePlaylistRenderer = ({ block, isEditMode, onBlockUpdate }) => {
  const model = useMemo(() => normalizePlaylistBlock(block), [block]);
  const [activeVideoId, setActiveVideoId] = useState(model.startVideoId);
  const [copied, setCopied] = useState(false);

  // Re-opening a saved deck, or editing the block, resets the card to the video
  // the author chose as its starting point.
  useEffect(() => {
    setActiveVideoId(model.startVideoId);
  }, [model.startVideoId, model.playlistId]);

  const theme = THEMES[model.theme] || THEMES.light;
  const accent = model.accentColor;
  const aspectRatio = ASPECT_RATIOS[model.aspectRatio] || ASPECT_RATIOS['16-9'];
  const activeItem = model.items.find((item) => item.videoId === activeVideoId) || null;
  const activeIndex = activeItem ? activeItem.position : model.startIndex;

  const embedUrl = playlistEmbedUrl({
    playlistId: model.playlistId,
    videoId: activeVideoId,
    index: activeVideoId ? null : model.startIndex
  });

  // The slide carries the note only — the address belongs in the deck's
  // reference list, the way an image's figure note works. (The card's share
  // line already gives the room a link to copy.) A card nobody has given
  // source details to shows no note at all, rather than a bare "(n.d.)".
  const showCitation = model.includeCitation && hasPlaylistCitationDetails(block);
  const citation = showCitation ? formatInText(buildVideoSourceMeta(block)) : '';

  const showPlayer = model.layout !== 'list';
  const hasList = model.items.length > 0;
  // With no entries there is nothing to list — but an author who chose a
  // layout with a list needs to be told why the rail is empty rather than
  // left wondering. The prompt is for the editor only; a deck being presented
  // or exported simply shows the player.
  const showListPrompt = model.layout !== 'player' && !hasList && isEditMode;
  const showList = model.layout !== 'player' && hasList;
  const isSideBySide = model.layout === 'split' && showPlayer && (showList || showListPrompt);

  const handleCopy = async () => {
    if (!model.playlistUrl) return;
    try {
      await navigator.clipboard.writeText(model.playlistUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard permission denied — the visible link is still selectable.
    }
  };

  // The one thing worth editing straight on the canvas: which video the card
  // opens on. Everything else lives in the block's editor modal.
  const handleSelect = (item) => {
    setActiveVideoId(item.videoId);
    if (isEditMode && onBlockUpdate) {
      onBlockUpdate({ ...block, startVideoId: item.videoId, startIndex: item.position });
    }
  };

  if (!model.playlistId) {
    return (
      <div className="my-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
        <div className="text-sm font-semibold text-slate-700">YouTube playlist</div>
        <p className="mt-1 text-sm text-slate-500">
          Paste a playlist link (any YouTube address containing <code>list=</code>) to build the card.
        </p>
      </div>
    );
  }

  return (
    <div className="my-6 youtube-playlist-block">
      <div
        className="overflow-hidden rounded-2xl border shadow-sm"
        style={{ backgroundColor: theme.surface, borderColor: theme.border }}
      >
        {/* Header — what this is, who made it, and how long it runs */}
        <div
          className="flex flex-wrap items-start justify-between gap-3 px-5 py-4"
          style={{ borderBottom: `1px solid ${theme.border}`, background: `linear-gradient(90deg, ${accent}14, transparent 60%)` }}
        >
          <div className="min-w-0 flex items-start gap-3">
            <span
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: accent }}
              aria-hidden="true"
            >
              <PlayGlyph />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
                  style={{ backgroundColor: `${accent}1f`, color: accent }}
                >
                  YouTube playlist
                </span>
                <span className="text-xs font-medium" style={{ color: theme.muted }}>
                  {playlistCountLabel({ itemCount: model.itemCount, startIndex: activeIndex })}
                </span>
              </div>
              <h4 className="mt-1 text-base font-semibold leading-snug" style={{ color: theme.heading }}>
                {model.title || 'Untitled playlist'}
              </h4>
              {model.channel && (
                <div className="text-sm" style={{ color: theme.body }}>{model.channel}</div>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors"
              style={{ borderColor: theme.border, color: theme.body, backgroundColor: theme.surface }}
            >
              {copied ? '✓ Link copied' : 'Copy link'}
            </button>
            <a
              href={model.playlistUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: accent }}
            >
              Open on YouTube
            </a>
          </div>
        </div>

        {model.description && (
          <p className="px-5 pt-4 text-sm leading-relaxed" style={{ color: theme.body }}>
            {model.description}
          </p>
        )}

        <div className={isSideBySide ? 'grid gap-0 md:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]' : ''}>
          {showPlayer && (
            <div className="p-5">
              <div
                className="relative w-full overflow-hidden rounded-xl bg-slate-900"
                style={{ aspectRatio }}
              >
                <iframe
                  key={embedUrl}
                  src={embedUrl}
                  className="absolute inset-0 h-full w-full border-0"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  referrerPolicy="strict-origin-when-cross-origin"
                  title={model.title ? `${model.title} — playlist player` : 'YouTube playlist player'}
                />
              </div>
              {activeItem?.title && (
                <div className="mt-3 text-sm font-medium" style={{ color: theme.heading }}>
                  <span style={{ color: accent }}>▶</span> {activeItem.title}
                  {activeItem.duration && (
                    <span className="ml-2 text-xs font-normal" style={{ color: theme.muted }}>
                      {activeItem.duration}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {showListPrompt && (
            <div
              className={isSideBySide ? 'p-5 md:border-l md:pl-4' : 'px-5 pt-3 pb-5'}
              style={isSideBySide ? { borderColor: theme.border } : undefined}
            >
              <div
                className="mb-2 text-[11px] font-semibold uppercase tracking-wide"
                style={{ color: theme.muted }}
              >
                In this playlist
              </div>
              <div
                className="rounded-xl border border-dashed p-3 text-xs leading-relaxed"
                style={{ borderColor: theme.border, backgroundColor: theme.rail, color: theme.muted }}
              >
                No videos listed yet. The player above still carries the whole playlist — add the
                running order in this block's editor (<span className="font-semibold">Fetch playlist</span>,
                or paste the video links) to show it here and in every export.
              </div>
            </div>
          )}

          {showList && (
            <div
              className={isSideBySide ? 'p-5 md:border-l md:pl-4' : 'px-5 pt-3 pb-5'}
              style={isSideBySide ? { borderColor: theme.border } : undefined}
            >
              <div
                className="mb-2 text-[11px] font-semibold uppercase tracking-wide"
                style={{ color: theme.muted }}
              >
                In this playlist
              </div>
              <ol
                className="youtube-playlist-track-list space-y-1 overflow-y-auto"
                style={{ maxHeight: isSideBySide ? '22rem' : '16rem', backgroundColor: theme.rail, borderRadius: '0.75rem', padding: '0.375rem' }}
              >
                {model.items.map((item) => {
                  const isActive = item.videoId === activeVideoId;
                  return (
                    <li key={item.id}>
                      <div
                        className="flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors"
                        style={{
                          backgroundColor: isActive ? theme.rowActive : 'transparent',
                          boxShadow: isActive ? `inset 3px 0 0 ${accent}` : 'none'
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleSelect(item)}
                          disabled={!item.videoId}
                          aria-current={isActive ? 'true' : undefined}
                          // The row truncates to one line; the full title stays reachable on hover
                          title={item.title || undefined}
                          className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-default"
                        >
                          <span
                            className="w-5 shrink-0 text-center text-[11px] font-semibold tabular-nums"
                            style={{ color: isActive ? accent : theme.muted }}
                          >
                            {item.position}
                          </span>
                          {model.showThumbnails && item.videoId && (
                            <img
                              src={item.src || videoThumbnailUrl(item.videoId)}
                              alt=""
                              loading="lazy"
                              className="h-8 w-14 shrink-0 rounded object-cover"
                              style={{ backgroundColor: '#0f172a' }}
                              onError={(event) => { event.currentTarget.style.visibility = 'hidden'; }}
                            />
                          )}
                          <span className="min-w-0 flex-1">
                            <span
                              className="block truncate text-sm leading-snug"
                              style={{ color: isActive ? theme.heading : theme.body, fontWeight: isActive ? 600 : 400 }}
                            >
                              {item.title || `Video ${item.position}`}
                            </span>
                            {item.note && (
                              <span className="block truncate text-[11px]" style={{ color: theme.muted }}>
                                {item.note}
                              </span>
                            )}
                          </span>
                          {item.duration && (
                            <span className="shrink-0 text-[11px] tabular-nums" style={{ color: theme.muted }}>
                              {item.duration}
                            </span>
                          )}
                        </button>
                        {item.videoId && (
                          <a
                            href={playlistWatchUrl({
                              playlistId: model.playlistId,
                              videoId: item.videoId,
                              index: item.position
                            })}
                            target="_blank"
                            rel="noreferrer"
                            title="Open this video on YouTube"
                            aria-label={`Open ${item.title || `video ${item.position}`} on YouTube`}
                            className="shrink-0 text-xs"
                            style={{ color: theme.muted }}
                          >
                            ↗
                          </a>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </div>

        {/* The share line: readable, selectable, and the same URL the button copies */}
        <div
          className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-xs"
          style={{ borderTop: `1px solid ${theme.border}`, backgroundColor: theme.rail, color: theme.muted }}
        >
          <span className="break-all">
            Share:{' '}
            <a href={model.playlistUrl} target="_blank" rel="noreferrer" style={{ color: accent }}>
              {model.playlistUrl}
            </a>
          </span>
          {model.items.length > 0 && (
            <span>{model.items.length} listed</span>
          )}
        </div>
      </div>

      {citation && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
          {citation}
        </div>
      )}
    </div>
  );
};

export default YouTubePlaylistRenderer;
